/**
 * Letters NFD cannot decompose: the diacritic is part of the glyph (a stroke,
 * slash or bar) rather than a separate combining mark, so stripping marks
 * leaves them untouched. Mapped explicitly to their base letters.
 *
 * Expand to two letters only where there is no single base letter (ß, æ, œ, þ);
 * otherwise map to the base letter — "ø" → "o", matching what NFD already does
 * to "ö". Substring matching handles the two-letter cases fine.
 */
const foldedLetters: Record<string, string> = {
  ß: "ss",
  æ: "ae",
  œ: "oe",
  đ: "d",
  ð: "d",
  ħ: "h",
  ı: "i",
  ĸ: "k",
  ł: "l",
  ŀ: "l",
  ŋ: "n",
  ø: "o",
  ſ: "s",
  ŧ: "t",
  þ: "th",
};

// Keys are escaped before going into the character class: `\`, `]`, `^` and `-`
// are special there, so an unescaped future entry would silently widen the class
// rather than fail.
const foldedLettersPattern = new RegExp(
  `[${Object.keys(foldedLetters)
    .join("")
    .replace(/[\\\]^-]/g, "\\$&")}]`,
  "g",
);

/**
 * Folds diacritics to their base letters (č → c, ä → a, ł → l) and lowercases,
 * so text matching is accent-insensitive. NFD splits an accented character into
 * base + combining mark; the combining-mark range is then stripped. Letters
 * with no such decomposition go through `foldedLetters`.
 */
export function foldText(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(foldedLettersPattern, (letter) => foldedLetters[letter]);
}
